import { FC, useCallback, useMemo, useReducer, useState } from 'react';
import { CATEGORIES, SUBCATEGORIES } from '../../common/categories';
import { useSelector } from 'react-redux';
import { fieldsReducer, initFieldsState, mapToSettings } from './mfaTokensReducer';
import {
    Token,
    PredefinedTokenKey,
    Tokens,
    MfaProviderIntegrationConfig,
    ExternalMfaProviderConfigValues,
    ExternalMfaProviderSettings,
    MfaProviderIntegrationId,
    TokenKey,
    ExternalMfaProviderSettingsValue
} from './types';
import MFATokenLayout from './MFATokenLayout';
import { useSaveSettings } from '../../hooks/useSaveSettings';
import * as actions from './mfaTokensActions';
import { SET_TOKEN_FIELD } from './mfaTokensActions';
import TokenTable, { TokenRow } from './components/TokenTable';
import {
    ENTRA_ID_MFA_TOKEN_FIELDS,
    IS_VISIBLE_FIELD,
    LAST_MODIFIED_FIELD,
    MICROSOFT_MFA_TOKEN_FIELDS
} from '../../common/fields';
import { Alert, Icons, showAlert, SnackAlert, SnackAlertContent } from '@silverfort/sf-components';
import DeleteTokenPopup from './components/DeleteTokenPopup';
import TestMFATokenPopup from './components/TestMFATokenPopup';
import { isUserPermitted } from '../../../utils';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { MFA_TOKEN_TO_TEST_FACTOR } from './consts';
import { useExternalMfaConfigs } from './api';
import { getObjectEntries, getObjectKeys } from '../../../../utils';

const { MFA_TOKENS } = CATEGORIES;
const { MICROSOFT_MFA_TOKEN, ENTRA_ID_MFA_TOKEN, FIDO2_MFA_TOKEN } = SUBCATEGORIES;

const filterVisibleFields = (tokens: Tokens) => {
    const visibleFields: Tokens = {};
    Object.keys(tokens).forEach((tokenKey) => {
        const token = tokens[tokenKey];
        if (token.fields?.[IS_VISIBLE_FIELD.ID]?.value) {
            visibleFields[tokenKey] = token;
        }
    });

    return visibleFields;
};

const getTableData = (
    configuredTokens: Record<string, Token>,
    configuredExternalMFAProviders: ExternalMfaProviderSettings,
    externalConfigs: MfaProviderIntegrationConfig[]
): TokenRow[] => {
    const nonMicrosoftTokens: TokenRow[] = Object.values(configuredTokens)
        .filter((token: Token) => token.id !== MICROSOFT_MFA_TOKEN.ID && token.id !== ENTRA_ID_MFA_TOKEN.ID)
        .map((token: Token) => ({
            id: token.id,
            name: token.name,
            lastModified: token.fields[LAST_MODIFIED_FIELD.ID].value,
            isExternal: false,
            isTestable: token.id !== FIDO2_MFA_TOKEN.ID
        }));

    const entraIdTokens: TokenRow[] = Object.values(configuredTokens)
        .filter((token: Token) => token.id === ENTRA_ID_MFA_TOKEN.ID)
        .filter((token: Token) => token.id !== MICROSOFT_MFA_TOKEN.ID)
        .flatMap((token: Token) =>
            token.fields[ENTRA_ID_MFA_TOKEN_FIELDS.ENTRA_ID_MFA_APPS.ID].value.tokens.map((entraToken) => ({
                id: token.id,
                name: token.name,
                subtitle: entraToken.applicationName,
                lastModified: entraToken.lastModified,
                entraTokenId: entraToken.id,
                isExternal: false,
                isTestable: false
            }))
        );
    const microsoftTokens: TokenRow[] = Object.values(configuredTokens)
        .filter((token: Token) => token.id === MICROSOFT_MFA_TOKEN.ID)
        .filter((token: Token) => token.id !== ENTRA_ID_MFA_TOKEN.ID)
        .flatMap((token: Token) =>
            token.fields[MICROSOFT_MFA_TOKEN_FIELDS.AAD_PUBLIC_KEY.ID].value.tenants.map((tenant) => ({
                tenantId: tenant.tenantId,
                tenantName: tenant.name,
                publicKey: tenant.publicKey,
                id: token.id,
                name: token.name,
                subtitle: tenant.name,
                lastModified: tenant.lastModified,
                isExternal: false,
                isTestable: true
            }))
        );

    const externalTokens: TokenRow[] = Object.entries<ExternalMfaProviderSettingsValue>(
        configuredExternalMFAProviders
    ).map(([id, provider]) => {
        const config = externalConfigs.find((c) => c.id === Number(id));
        return {
            id: config?.id ?? Number(id),
            name: config?.displayName ?? provider.name,
            lastModified: provider.lastModified,
            isExternal: true,
            isTestable: false
        };
    });

    return [...nonMicrosoftTokens, ...microsoftTokens, ...entraIdTokens, ...externalTokens].sort((a, b) =>
        a.name.localeCompare(b.name)
    );
};

const isMicrosoftToken = (tokenKey: PredefinedTokenKey) =>
    tokenKey === MICROSOFT_MFA_TOKEN.ID || tokenKey === ENTRA_ID_MFA_TOKEN.ID;

interface Props {
    saveFunc: (settings: Record<string, any>, metadata: any, includeFullError?: boolean) => Promise<any>;
    user: unknown;
}

const MFATokens: FC<Props> = ({ saveFunc, user }) => {
    const settings = useSelector((state: any) => state.settings.settings);
    const [fields, dispatch] = useReducer(fieldsReducer, { settings }, initFieldsState);
    const visibleFields = fields as Tokens;

    const [isMFATokenVisible, setIsMFATokenVisible] = useState(false);
    const [editToken, setEditToken] = useState<TokenKey>();
    const [editTenantId, setEditTenantId] = useState<string | undefined>();
    const [editEntraIdToken, setEditEntraIdToken] = useState<string | undefined>();

    const [deleteToken, setDeleteToken] = useState<TokenRow | undefined>();

    const [selectedMfaToken, setSelectedMfaToken] = useState<TokenKey | undefined>(undefined);

    const [snackAlertMessage, setSnackAlertMessage] = useState<string>();

    const [testTokenId, setTestTokenId] = useState<PredefinedTokenKey | undefined>();
    const [testTenantName, setTestTenantName] = useState<string | undefined>();
    const { data: externalConfigs } = useExternalMfaConfigs();

    const allowTestToken = isUserPermitted(user, 'testMFATokens', ROLES.EDITOR.value);

    const configuredTokens: Tokens = filterVisibleFields(visibleFields);
    const alreadyConfiguredExternalMfaProviders: ExternalMfaProviderSettings = useMemo(
        () => (settings.externalMFAProviders || {}) as ExternalMfaProviderSettings,
        [settings.externalMFAProviders]
    );

    const tableData = getTableData(configuredTokens, alreadyConfiguredExternalMfaProviders, externalConfigs || []);

    const [save] = useSaveSettings(saveFunc, settings, mapToSettings, fields, selectedMfaToken, dispatch);

    const options = useMemo(() => {
        const legacyOptions = Object.values(visibleFields)
            .map((token: Token) => ({
                title: token.name,
                value: token.id,
                disabled: !isMicrosoftToken(token.id) && !!token.fields[IS_VISIBLE_FIELD.ID].value,
                subtitle: !isMicrosoftToken(token.id)
                    ? token.fields[IS_VISIBLE_FIELD.ID].value
                        ? 'Token already configured'
                        : ''
                    : '',
                isExternal: false
            }))
            .sort((a, b) => Number(a.disabled) - Number(b.disabled));

        const configuredExternalIds = new Set(
            getObjectKeys(alreadyConfiguredExternalMfaProviders).map((id) => Number(id))
        );
        const externalOptions = (externalConfigs || []).map((config) => {
            const isConfigured = configuredExternalIds.has(config.id);
            return {
                value: config.id,
                title: config.displayName,
                disabled: isConfigured,
                subtitle: isConfigured ? 'Token already configured' : '',
                isExternal: true
            };
        });

        return [...legacyOptions, ...externalOptions];
    }, [visibleFields, externalConfigs, alreadyConfiguredExternalMfaProviders]);

    const handleEdit = (token: TokenRow) => {
        if (token.id === MICROSOFT_MFA_TOKEN.ID && token.tenantId) {
            setEditTenantId(token.tenantId);
        }

        if (token.id === ENTRA_ID_MFA_TOKEN.ID && token.entraTokenId) {
            setEditEntraIdToken(token.entraTokenId);
        }

        setIsMFATokenVisible(true);
        setEditToken(token.id);
    };

    const handleTest = useCallback(
        (tokenId: PredefinedTokenKey, tenantName?: string) => {
            if (allowTestToken) {
                if (isMicrosoftToken(tokenId) && tenantName) {
                    setTestTenantName(tenantName);
                }

                setTestTokenId(tokenId);
            }
        },
        [allowTestToken]
    );

    const handleCreate = () => {
        setIsMFATokenVisible(true);
    };

    const handleCloseTestTokenPopup = () => {
        setTestTokenId(undefined);
        setTestTenantName(undefined);
    };

    const handleCancel = () => {
        setIsMFATokenVisible(false);
        setEditToken(undefined);
        setSelectedMfaToken(undefined);
        setEditTenantId(undefined);
        setEditEntraIdToken(undefined);
    };

    const handleOpenDeletePopup = (token: TokenRow) => {
        setDeleteToken(token);
    };

    const handleCloseDeletePopup = () => {
        setDeleteToken(undefined);
    };

    const deleteExternalMfaProvider = async (token: TokenRow) => {
        const config = externalConfigs?.find((c) => c.id === token.id);
        if (!config) return;

        const updatedProvidersObj = { ...alreadyConfiguredExternalMfaProviders };
        delete updatedProvidersObj[config.id];

        try {
            await saveFunc({ externalMFAProviders: updatedProvidersObj }, null);
            handleCloseDeletePopup();
            setSnackAlertMessage(`${token.name} was removed`);
        } catch (error: unknown) {
            showAlert('Failed to delete token provider integration', 'error');
        }
    };

    const deletePredefinedTokenProvider = (token: TokenRow) => {
        let actionType: string;
        let payload: string | undefined;

        if (token.id === MICROSOFT_MFA_TOKEN.ID) {
            actionType = actions.DELETE_MICROSOFT_TENANT;
            payload = token.tenantId;
        } else if (token.id === ENTRA_ID_MFA_TOKEN.ID) {
            actionType = actions.DELETE_ENTRA_ID_MFA_TOKEN;
            payload = token.entraTokenId;
        } else {
            actionType = actions.DELETE_TOKEN;
            payload = token.id as string; // todo: see if we want to remove the as
        }

        // @ts-ignore - Suppress TypeScript error as the action type is not recognized by TypeScript
        dispatch({ type: actionType, payload });
        save(true);
        handleCloseDeletePopup();
        setSnackAlertMessage(`${token.name} was removed`);
    };

    const handleDelete = async (token: TokenRow) => {
        if (token.isExternal) {
            await deleteExternalMfaProvider(token);
            return;
        }

        deletePredefinedTokenProvider(token);
    };

    const handleSave = () => {
        setIsMFATokenVisible(false);
        setEditToken(undefined);
        setSelectedMfaToken(undefined);

        // @ts-ignore - Suppress TypeScript error as the action type is not recognized by TypeScript
        dispatch({
            type: SET_TOKEN_FIELD,
            payload: { tokenId: selectedMfaToken, field: IS_VISIBLE_FIELD.ID, value: true }
        });
        // @ts-ignore - Suppress TypeScript error as the action type is not recognized by TypeScript
        dispatch({
            type: SET_TOKEN_FIELD,
            payload: {
                tokenId: selectedMfaToken,
                field: LAST_MODIFIED_FIELD.ID,
                value: Date.now()
            }
        });
        save(true);
    };

    const handleExternalConfigSave = useCallback(
        async (integrationId: MfaProviderIntegrationId, data: ExternalMfaProviderConfigValues) => {
            const config = externalConfigs?.find((c) => c.id === integrationId);
            if (!config) return;

            const updatedExternalMfaProviders: ExternalMfaProviderSettings = {
                ...alreadyConfiguredExternalMfaProviders,
                [config.id]: {
                    name: config.name,
                    values: data,
                    lastModified: Date.now()
                }
            };

            try {
                await saveFunc({ externalMFAProviders: updatedExternalMfaProviders }, null);
                setSnackAlertMessage(`${config.displayName} configuration saved`);
                setIsMFATokenVisible(false);
                setSelectedMfaToken(undefined);
            } catch (error: unknown) {
                showAlert('Failed to save token provider integration', 'error');
            }
        },
        [externalConfigs, alreadyConfiguredExternalMfaProviders, saveFunc]
    );

    return (
        <div className="font-sf bg-sf-white w-full h-[calc(100vh-62px)] overflow-y-auto">
            <div className="p-6">
                <div className="text-16 h-[40px] font-medium text-sf-gray-800 mb-4">{MFA_TOKENS.NAME}</div>

                {tableData.length === 0 || isMFATokenVisible ? (
                    <MFATokenLayout
                        setSelectedMfaToken={setSelectedMfaToken}
                        selectedMfaToken={selectedMfaToken}
                        onCancel={handleCancel}
                        listOptions={options}
                        save={handleSave}
                        editToken={editToken}
                        visibleFields={visibleFields}
                        dispatch={dispatch}
                        editTenantId={editTenantId}
                        editEntraIdToken={editEntraIdToken}
                        configuredTokens={tableData}
                        externalConfigs={externalConfigs || []}
                        onExternalConfigSave={handleExternalConfigSave}
                        alreadyConfiguredExternalMFAProviders={alreadyConfiguredExternalMfaProviders}
                    />
                ) : (
                    <TokenTable
                        tableData={tableData}
                        handleEdit={handleEdit}
                        handleDelete={handleOpenDeletePopup}
                        handleCreate={handleCreate}
                        onTest={handleTest}
                        allowTestToken={allowTestToken}
                    />
                )}

                <Alert />
                <DeleteTokenPopup
                    open={deleteToken !== undefined}
                    handleConfirm={() => handleDelete(deleteToken!)}
                    handleCancel={handleCloseDeletePopup}
                />
                <TestMFATokenPopup
                    open={testTokenId !== undefined}
                    onConfirm={handleCloseTestTokenPopup}
                    onCancel={handleCloseTestTokenPopup}
                    testFactor={MFA_TOKEN_TO_TEST_FACTOR[testTokenId!]}
                    testFactorTenantId={testTenantName}
                />

                {snackAlertMessage && (
                    <SnackAlert
                        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
                        open
                        onClose={() => setSnackAlertMessage(undefined)}>
                        <SnackAlertContent
                            iconLeft={Icons.Delete}
                            message={snackAlertMessage}
                            onClose={() => setSnackAlertMessage(undefined)}
                        />
                    </SnackAlert>
                )}
            </div>
        </div>
    );
};

export default MFATokens;
